/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Loading Banner - Persistent loading indicator below header
 * Updated: 2026-01-09
 */

import React from 'react';
import { X } from 'lucide-react';

interface LoadingBannerProps {
  message: string;
  progress?: number; // 0-100
  onCancel?: () => void;
  showProgressBar?: boolean;
}

const LoadingBanner: React.FC<LoadingBannerProps> = ({
  message,
  progress,
  onCancel,
  showProgressBar = false,
}) => {
  return (
    <div className="fixed top-0 left-0 right-0 z-40 animate-in fade-in slide-in-from-top-0 duration-300">
      {/* Header placeholder to offset below existing header */}
      <div className="h-14"></div>

      {/* Loading Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {/* Spinner */}
            <div className="flex-shrink-0">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            </div>

            {/* Message */}
            <p className="text-sm font-medium text-blue-900 truncate">{message}</p>
          </div>

          {/* Progress Bar (optional) */}
          {showProgressBar && progress !== undefined && (
            <div className="mt-2 bg-blue-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-blue-600 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}

          {/* Progress percentage */}
          {showProgressBar && progress !== undefined && (
            <p className="text-xs text-blue-600 mt-1">{progress}% complete</p>
          )}
        </div>

        {/* Cancel Button */}
        {onCancel && (
          <button
            onClick={onCancel}
            className="flex-shrink-0 p-1.5 hover:bg-blue-200 rounded-md transition-colors"
            title="Cancel"
          >
            <X className="w-4 h-4 text-blue-700" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LoadingBanner;
